// AllRequests.js - Professional Enterprise Ticket Management (FIXED)

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useHistory } from 'react-router-dom';
import Axios from 'axios';
import { Host } from '../../App';
import './AllRequests.css';

const AllRequests = () => {
  // Data States
  const [tickets, setTickets] = useState([]);
  const [filteredTickets, setFilteredTickets] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Search & Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    priority: '',
    type: '',
    category: '',
    approval_status: '',
    assigned_to: '',
    requested_by: '',
    date_from: '',
    date_to: '',
    overdue: false,
    follow_up: false
  });

  // Quick Filters
  const [activeQuickFilter, setActiveQuickFilter] = useState('all');

  // Recurring parent visibility toggle
  const [showApprovedParents, setShowApprovedParents] = useState(false);

  // Sorting
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);

  // Selection
  const [selectedTickets, setSelectedTickets] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  // View Mode
  const [viewMode, setViewMode] = useState('table');

  // User Role
  const [isMaintenance, setIsMaintenance] = useState(false);
  const [isManager, setIsManager] = useState(false);

  // Modals
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [selectedTicketForApproval, setSelectedTicketForApproval] = useState(null);

  // Modal Data
  const [approvalData, setApprovalData] = useState({
    assigned_completion_date: '',
    manager_comments: ''
  });
  const [rejectionReason, setRejectionReason] = useState('');
  const [bulkAction, setBulkAction] = useState('status');
  const [bulkStatus, setBulkStatus] = useState('');
  const [bulkPriority, setBulkPriority] = useState('');
  const [bulkAssignee, setBulkAssignee] = useState('');

  // Loading States
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  // Statistics
  const [statistics, setStatistics] = useState({
    total: 0,
    open: 0,
    inProgress: 0,
    completed: 0,
    overdue: 0,
    critical: 0,
    rejected: 0,
    pendingApproval: 0
  });

  // Refs
  const searchInputRef = useRef(null);
  const tableRef = useRef(null);

  const token = window.localStorage.getItem('acessToken');
  const userName = JSON.parse(window.localStorage.getItem('Name'));
  const history = useHistory();

  // ===== HELPER FUNCTIONS =====

  // Check if ticket has rejected completion
  const isCompletionRejected = (ticket) => {
    return ticket.approval_status === 'Rejected' && ticket.approval_type === 'completion';
  };

  // Get status label for display
  const getStatusLabel = (ticket) => {
    if (isCompletionRejected(ticket)) {
      return 'Completion Rejected';
    }
    const labels = {
      'open': 'Open',
      'in_progress': 'In Progress',
      'review': 'Review',
      'completed': 'Completed',
      'closed': 'Closed',
      'cancelled': 'Cancelled'
    };
    return labels[ticket.status] || ticket.status || 'Unknown';
  };

  const getDueDate = (ticket) => {
    if (ticket.type === 'prescheduled' && ticket.schedule_start_date) {
      return ticket.schedule_start_date;
    }
    return ticket.assigned_completion_date || ticket.tentative_completion_date || null;
  };

  const getDaysRemaining = (ticket) => {
    const dueDate = getDueDate(ticket);
    if (!dueDate) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = new Date(dueDate);
    due.setHours(0, 0, 0, 0);
    return Math.ceil((due - today) / (1000 * 60 * 60 * 24));
  };

  const isOverdue = (ticket) => {
    if (isCompletionRejected(ticket)) return false;
    if (['completed', 'closed', 'cancelled'].includes(ticket.status)) return false;
    const daysRemaining = getDaysRemaining(ticket);
    return daysRemaining !== null && daysRemaining < 0;
  };

  const getTicketTypeLabel = (ticket) => {
    if (ticket.type === 'prescheduled') {
      if (ticket.is_recurring) {
        return `Recurring (${ticket.recurrence_type || 'monthly'})`;
      }
      return 'Pre-scheduled';
    }
    return 'Emergent';
  };

  // Unique list of requesters for the dropdown filter
  const getUniqueRequesters = () => {
    if (!Array.isArray(tickets) || tickets.length === 0) return [];
    return [...new Set(tickets.map(t => t.requested_by).filter(Boolean))].sort();
  };

  // ===== EFFECTS =====
  useEffect(() => {
    fetchTickets();
    checkUserRole();

    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
    };
    window.addEventListener('keydown', handleKeyDown);

    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    applyAllFilters();
  }, [tickets, searchTerm, filters, activeQuickFilter, sortConfig, showApprovedParents]);

  // ===== API CALLS =====
  const checkUserRole = async () => {
    try {
      const response = await Axios.get(`https://` + Host + `/api/user-info`, {
        params: { token }
      });
      if (response.data) {
        setIsMaintenance(response.data.Section === 'Maintenance' || response.data.Section === 'Manager');
        setIsManager(response.data.Stage === '2' || response.data.Ranked === 'Admin' || response.data.Section === 'Manager');
      }
    } catch (error) {
      console.error('Error checking user role:', error);
    }
  };

  const fetchTickets = async () => {
    setIsLoading(true);
    try {
      const response = await Axios.get(`https://` + Host + `/maintenance/api/tickets`, {
        params: {
          token,
          status: filters.status || undefined,
          priority: filters.priority || undefined,
          type: filters.type || undefined,
          date_from: filters.date_from || undefined,
          date_to: filters.date_to || undefined
        }
      });

      if (response.data) {
        const ticketData = response.data.data || response.data;
        setTickets(Array.isArray(ticketData) ? ticketData : []);
        calculateStatistics(Array.isArray(ticketData) ? ticketData : []);
      }
    } catch (error) {
      console.error('Error fetching tickets:', error);
      setTickets([]);
    } finally {
      setIsLoading(false);
    }
  };

  // ===== STATISTICS =====
  const calculateStatistics = (ticketData) => {
    const stats = {
      total: ticketData.length,
      open: ticketData.filter(t => t.status === 'open').length,
      inProgress: ticketData.filter(t => t.status === 'in_progress').length,
      completed: ticketData.filter(t => t.status === 'completed' || t.status === 'closed').length,
      overdue: ticketData.filter(t => isOverdue(t)).length,
      critical: ticketData.filter(t => t.priority === 'Critical' && t.status !== 'completed' && t.status !== 'closed').length,
      rejected: ticketData.filter(t => isCompletionRejected(t)).length,
      pendingApproval: ticketData.filter(t => t.approval_status === 'Pending').length
    };
    setStatistics(stats);
  };

  // ===== FILTERING & SORTING =====
  const applyAllFilters = useCallback(() => {
    let result = [...tickets];

    const parentApprovalMap = {};
    tickets.forEach(t => {
      const isParentTicket = t.is_recurring && !t.recurrence_parent_id;
      if (isParentTicket) {
        parentApprovalMap[t.id] = (t.approval_status === 'Approved' || t.approval_status === 'approved');
      }
    });

    result = result.filter(t => {
      const isParent = t.is_recurring && !t.recurrence_parent_id;
      const isInstance = t.is_recurring && t.recurrence_parent_id;

      if (isParent) {
        const approved = (t.approval_status === 'Approved' || t.approval_status === 'approved');
        if (!approved) return true;
        return showApprovedParents;
      }

      if (isInstance) {
        return !!parentApprovalMap[t.recurrence_parent_id];
      }

      return true;
    });

    // Quick Filters
    switch (activeQuickFilter) {
      case 'open':
        result = result.filter(t => t.status === 'open');
        break;
      case 'in_progress':
        result = result.filter(t => t.status === 'in_progress');
        break;
      case 'overdue':
        result = result.filter(t => isOverdue(t));
        break;
      case 'pending_approval':
        result = result.filter(t => t.approval_status === 'Pending');
        break;
      case 'critical':
        result = result.filter(t => t.priority === 'Critical');
        break;
      case 'my_requests':
        result = result.filter(t => t.requested_by === userName);
        break;
      case 'unassigned':
        result = result.filter(t => !t.assigned_to);
        break;
      case 'rejected':
        result = result.filter(t => isCompletionRejected(t));
        break;
      default:
        break;
    }

    // Advanced Filters
    if (filters.status) {
      result = result.filter(t => t.status === filters.status);
    }
    if (filters.priority) {
      result = result.filter(t => t.priority === filters.priority);
    }
    if (filters.type) {
      result = result.filter(t => t.type === filters.type);
    }
    if (filters.category) {
      result = result.filter(t => t.maintenance_category === filters.category);
    }
    if (filters.approval_status) {
      result = result.filter(t => t.approval_status === filters.approval_status);
    }
    if (filters.assigned_to) {
      result = result.filter(t => t.assigned_to?.toLowerCase().includes(filters.assigned_to.toLowerCase()));
    }
    if (filters.requested_by) {
      result = result.filter(t => t.requested_by === filters.requested_by);
    }
    if (filters.date_from) {
      result = result.filter(t => new Date(t.created_at) >= new Date(filters.date_from));
    }
    if (filters.date_to) {
      result = result.filter(t => new Date(t.created_at) <= new Date(filters.date_to));
    }
    if (filters.overdue) {
      result = result.filter(t => isOverdue(t));
    }
    if (filters.follow_up) {
      result = result.filter(t => t.follow_up_needed === 'Yes');
    }

    // Search
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(ticket =>
        ticket.ticket_number?.toLowerCase().includes(term) ||
        ticket.issue_description?.toLowerCase().includes(term) ||
        ticket.requested_by?.toLowerCase().includes(term) ||
        ticket.maintenance_category?.toLowerCase().includes(term) ||
        ticket.assigned_to?.toLowerCase().includes(term) ||
        ticket.equipment_maintenance?.toLowerCase().includes(term) ||
        ticket.facility_maintenance?.toLowerCase().includes(term)
      );
    }

    // Sorting
    result.sort((a, b) => {
      const aVal = a[sortConfig.key] || '';
      const bVal = b[sortConfig.key] || '';

      if (sortConfig.key === 'created_at' || sortConfig.key === 'request_date') {
        return sortConfig.direction === 'asc'
          ? new Date(aVal) - new Date(bVal)
          : new Date(bVal) - new Date(aVal);
      }

      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

    setFilteredTickets(result);
    setCurrentPage(1);
    setSelectedTickets([]);
    setSelectAll(false);
  }, [tickets, activeQuickFilter, filters, searchTerm, sortConfig, showApprovedParents, userName]);

  const handleQuickFilter = (filter) => {
    setActiveQuickFilter(activeQuickFilter === filter ? 'all' : filter);
  };

  const handleFilterChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const clearAllFilters = () => {
    setFilters({
      status: '',
      priority: '',
      type: '',
      category: '',
      approval_status: '',
      assigned_to: '',
      requested_by: '',
      date_from: '',
      date_to: '',
      overdue: false,
      follow_up: false
    });
    setSearchTerm('');
    setActiveQuickFilter('all');
    setShowAdvancedFilters(false);
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (activeQuickFilter !== 'all') count++;
    Object.values(filters).forEach(val => {
      if (val && val !== false) count++;
    });
    if (searchTerm) count++;
    return count;
  };

  // ===== SELECTION =====
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedTickets([]);
      setSelectAll(false);
    } else {
      setSelectedTickets(currentItems.map(t => t.id));
      setSelectAll(true);
    }
  };

  const handleSelectTicket = (id) => {
    setSelectedTickets(prev => {
      const newSelection = prev.includes(id)
        ? prev.filter(tid => tid !== id)
        : [...prev, id];
      setSelectAll(newSelection.length === currentItems.length);
      return newSelection;
    });
  };

  // ===== BULK ACTIONS =====
  const handleBulkAction = async () => {
    if (selectedTickets.length === 0) {
      alert('Please select at least one ticket');
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = { ticketIds: selectedTickets };

      if (bulkAction === 'status') {
        payload.status = bulkStatus;
      } else if (bulkAction === 'priority') {
        payload.priority = bulkPriority;
      } else if (bulkAction === 'assign') {
        payload.assigned_to = bulkAssignee;
      }

      await Axios.patch(`https://` + Host + `/maintenance/api/tickets/bulk`, payload, {
        params: { token }
      });

      alert(`Successfully updated ${selectedTickets.length} tickets`);
      setShowBulkModal(false);
      setSelectedTickets([]);
      fetchTickets();
    } catch (error) {
      console.error('Error performing bulk action:', error);
      alert(error.response?.data?.error || 'Failed to update tickets');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ===== APPROVAL ACTIONS =====
  const openApprovalModal = (ticket) => {
    setSelectedTicketForApproval(ticket);
    const defaultDate = ticket.type === 'prescheduled'
      ? ticket.schedule_start_date
      : ticket.tentative_completion_date;
    setApprovalData({
      assigned_completion_date: defaultDate || '',
      manager_comments: ''
    });
    setShowApprovalModal(true);
  };

  const openRejectModal = (ticket) => {
    setSelectedTicketForApproval(ticket);
    setRejectionReason('');
    setShowRejectModal(true);
  };

  const handleApprove = async () => {
    if (!approvalData.assigned_completion_date) {
      alert('Please select a completion date');
      return;
    }

    setIsSubmitting(true);
    try {
      await Axios.post(`https://` + Host + `/maintenance/api/tickets/approve`, {
        token,
        ticketId: selectedTicketForApproval.id,
        action: 'approve',
        approved_by: userName,
        assigned_completion_date: approvalData.assigned_completion_date,
        manager_comments: approvalData.manager_comments,
        approval_type: selectedTicketForApproval.approval_type || 'new'
      });

      alert('Request approved successfully!');
      setShowApprovalModal(false);
      fetchTickets();
    } catch (error) {
      console.error('Error approving ticket:', error);
      alert(error.response?.data?.error || 'Failed to approve ticket');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      alert('Please provide a rejection reason');
      return;
    }

    setIsSubmitting(true);
    try {
      // Determine the correct approval type
      let approvalType = selectedTicketForApproval.approval_type || 'new';

      if (selectedTicketForApproval.completion_notes && selectedTicketForApproval.completion_notes !== '') {
        approvalType = 'completion';
      } else if (selectedTicketForApproval.pending_data && selectedTicketForApproval.pending_data !== '') {
        try {
          const pendingData = typeof selectedTicketForApproval.pending_data === 'string'
            ? JSON.parse(selectedTicketForApproval.pending_data)
            : selectedTicketForApproval.pending_data;

          if (pendingData && pendingData.completion_notes) {
            approvalType = 'completion';
          } else {
            approvalType = 'modification';
          }
        } catch (e) {
          if (typeof selectedTicketForApproval.pending_data === 'string' &&
            selectedTicketForApproval.pending_data.includes('completion_notes')) {
            approvalType = 'completion';
          } else {
            approvalType = 'modification';
          }
        }
      } else if (selectedTicketForApproval.status === 'completed' || selectedTicketForApproval.status === 'review') {
        approvalType = 'completion';
      }

      await Axios.post(`https://` + Host + `/maintenance/api/tickets/approve`, {
        token,
        ticketId: selectedTicketForApproval.id,
        action: 'reject',
        approved_by: userName,
        manager_comments: rejectionReason,
        approval_type: approvalType
      });

      alert('Request rejected');
      setShowRejectModal(false);
      fetchTickets();
    } catch (error) {
      console.error('Error rejecting ticket:', error);
      alert(error.response?.data?.error || 'Failed to reject ticket');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ===== EXPORT =====
  const handleExport = async (format) => {
    setExportLoading(true);
    try {
      const response = await Axios.get(`https://` + Host + `/maintenance/api/tickets/export`, {
        params: {
          token,
          format,
          ids: selectedTickets.length > 0 ? selectedTickets.join(',') : undefined
        },
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `maintenance-tickets-${new Date().toISOString().slice(0, 10)}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      setShowExportModal(false);
    } catch (error) {
      console.error('Error exporting tickets:', error);
      alert('Failed to export tickets');
    } finally {
      setExportLoading(false);
    }
  };

  // ===== STYLING HELPERS =====
  const getStatusColor = (status) => {
    const colors = {
      'open': { bg: '#fef3c7', text: '#92400e', border: '#fcd34d' },
      'in_progress': { bg: '#dbeafe', text: '#1e40af', border: '#93c5fd' },
      'review': { bg: '#ede9fe', text: '#5b21b6', border: '#c4b5fd' },
      'completed': { bg: '#d1fae5', text: '#065f46', border: '#6ee7b7' },
      'closed': { bg: '#e2e8f0', text: '#475569', border: '#cbd5e1' },
      'cancelled': { bg: '#fee2e2', text: '#991b1b', border: '#fca5a5' }
    };
    return colors[status] || { bg: '#f1f5f9', text: '#475569', border: '#cbd5e1' };
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'Critical': { bg: '#fee2e2', text: '#991b1b', icon: '🔴' },
      'High': { bg: '#fef3c7', text: '#92400e', icon: '🟠' },
      'Medium': { bg: '#dbeafe', text: '#1e40af', icon: '🟡' },
      'Low': { bg: '#f1f5f9', text: '#475569', icon: '🟢' }
    };
    return colors[priority] || { bg: '#f1f5f9', text: '#475569', icon: '⚪' };
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = now - date;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
  };

  // ===== PAGINATION =====
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredTickets.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredTickets.length / itemsPerPage);

  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  };

  // ===== GET ROW CLASS BASED ON STATUS =====
  const getRowClass = (ticket) => {
    const classes = ['ar-table-row'];

    if (selectedTickets.includes(ticket.id)) {
      classes.push('selected');
    }

    if (isCompletionRejected(ticket)) {
      classes.push('status-rejected');
    } else if (ticket.status === 'cancelled') {
      classes.push('status-cancelled');
    } else if (ticket.status === 'completed' || ticket.status === 'closed') {
      classes.push('status-completed');
    } else if (ticket.status === 'in_progress') {
      classes.push('status-in-progress');
    } else if (ticket.status === 'open') {
      classes.push('status-open');
    } else if (ticket.status === 'review') {
      classes.push('status-review');
    }

    if (isOverdue(ticket) && !isCompletionRejected(ticket)) {
      classes.push('overdue');
    }

    return classes.join(' ');
  };

  // ===== RENDER =====
  return (
    <div className="ar-all-requests-container">
      {/* Search and Quick Filters */}
      <div className="ar-search-section">
        <div className="ar-search-bar">
          <i className="fas fa-search ar-search-icon"></i>
          <input
            ref={searchInputRef}
            type="text"
            className="ar-search-input"
            placeholder="Search tickets by number, description, requester, assignee..."
            value={searchTerm}
            onChange={handleSearch}
          />
          {searchTerm && (
            <button
              className="ar-search-clear"
              onClick={() => setSearchTerm('')}
            >
              <i className="fas fa-times"></i>
            </button>
          )}
          <div className="ar-search-shortcut">
            <kbd>Ctrl</kbd> + <kbd>K</kbd>
          </div>
        </div>

        <div className="ar-quick-filters">
          <button
            className={`ar-quick-filter-btn ${activeQuickFilter === 'all' ? 'active' : ''}`}
            onClick={() => handleQuickFilter('all')}
          >
            All ({statistics.total})
          </button>
          <button
            className={`ar-quick-filter-btn ${activeQuickFilter === 'open' ? 'active' : ''}`}
            onClick={() => handleQuickFilter('open')}
          >
            Open ({statistics.open})
          </button>
          <button
            className={`ar-quick-filter-btn ${activeQuickFilter === 'in_progress' ? 'active' : ''}`}
            onClick={() => handleQuickFilter('in_progress')}
          >
            In Progress ({statistics.inProgress})
          </button>
          <button
            className={`ar-quick-filter-btn ${activeQuickFilter === 'overdue' ? 'active' : ''}`}
            onClick={() => handleQuickFilter('overdue')}
          >
            Overdue ({statistics.overdue})
          </button>
          <button
            className={`ar-quick-filter-btn ${activeQuickFilter === 'pending_approval' ? 'active' : ''}`}
            onClick={() => handleQuickFilter('pending_approval')}
          >
            Pending Approval ({statistics.pendingApproval})
          </button>
          <button
            className={`ar-quick-filter-btn ${activeQuickFilter === 'rejected' ? 'active' : ''}`}
            onClick={() => handleQuickFilter('rejected')}
          >
            Rejected ({statistics.rejected})
          </button>
          <button
            className={`ar-quick-filter-btn ${activeQuickFilter === 'critical' ? 'active' : ''}`}
            onClick={() => handleQuickFilter('critical')}
          >
            Critical ({statistics.critical})
          </button>
          {userName && (
            <button
              className={`ar-quick-filter-btn ${activeQuickFilter === 'my_requests' ? 'active' : ''}`}
              onClick={() => handleQuickFilter('my_requests')}
            >
              My Requests
            </button>
          )}
          <button
            className={`ar-quick-filter-btn ${activeQuickFilter === 'unassigned' ? 'active' : ''}`}
            onClick={() => handleQuickFilter('unassigned')}
          >
            Unassigned
          </button>
        </div>

        <div className="ar-filter-actions">
          <button
            className="ar-btn ar-btn-outline ar-btn-sm"
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
          >
            <i className={`fas fa-${showAdvancedFilters ? 'times' : 'filter'}`}></i>
            {showAdvancedFilters ? 'Hide Filters' : 'Advanced Filters'}
            {getActiveFilterCount() > 0 && (
              <span className="ar-filter-count">{getActiveFilterCount()}</span>
            )}
          </button>
          {getActiveFilterCount() > 0 && (
            <button
              className="ar-btn ar-btn-ghost ar-btn-sm"
              onClick={clearAllFilters}
            >
              Clear All Filters
            </button>
          )}
          <label className="ar-checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '6px', margin: '0 8px' }}>
            <input
              type="checkbox"
              checked={showApprovedParents}
              onChange={(e) => setShowApprovedParents(e.target.checked)}
            />
            <span>Show approved recurring parents</span>
          </label>
          <div className="ar-items-per-page">
            <span>Show</span>
            <select
              value={itemsPerPage}
              onChange={(e) => setItemsPerPage(Number(e.target.value))}
              className="ar-select-sm"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <span>entries</span>
          </div>
        </div>
      </div>

      {/* Advanced Filters Panel */}
      {showAdvancedFilters && (
        <div className="ar-advanced-filters">
          <div className="ar-filter-grid">
            <div className="ar-filter-group">
              <label>Status</label>
              <select name="status" value={filters.status} onChange={handleFilterChange}>
                <option value="">All Statuses</option>
                <option value="open">Open</option>
                <option value="in_progress">In Progress</option>
                <option value="review">Review</option>
                <option value="completed">Completed</option>
                <option value="closed">Closed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div className="ar-filter-group">
              <label>Priority</label>
              <select name="priority" value={filters.priority} onChange={handleFilterChange}>
                <option value="">All Priorities</option>
                <option value="Critical">Critical</option>
                <option value="High">High</option>
                <option value="Medium">Medium</option>
                <option value="Low">Low</option>
              </select>
            </div>
            <div className="ar-filter-group">
              <label>Type</label>
              <select name="type" value={filters.type} onChange={handleFilterChange}>
                <option value="">All Types</option>
                <option value="emergent">Emergent</option>
                <option value="prescheduled">Pre-scheduled</option>
              </select>
            </div>
            <div className="ar-filter-group">
              <label>Approval Status</label>
              <select name="approval_status" value={filters.approval_status} onChange={handleFilterChange}>
                <option value="">All</option>
                <option value="Pending">Pending</option>
                <option value="Approved">Approved</option>
                <option value="Rejected">Rejected</option>
              </select>
            </div>
            <div className="ar-filter-group">
              <label>Assigned To</label>
              <input
                type="text"
                name="assigned_to"
                placeholder="Filter by assignee..."
                value={filters.assigned_to}
                onChange={handleFilterChange}
              />
            </div>
            <div className="ar-filter-group">
              <label>Requested By</label>
              <select name="requested_by" value={filters.requested_by} onChange={handleFilterChange}>
                <option value="">All Requesters</option>
                {getUniqueRequesters().map((name, index) => (
                  <option key={index} value={name}>{name}</option>
                ))}
              </select>
            </div>
            <div className="ar-filter-group">
              <label>Date From</label>
              <input
                type="date"
                name="date_from"
                value={filters.date_from}
                onChange={handleFilterChange}
              />
            </div>
            <div className="ar-filter-group">
              <label>Date To</label>
              <input
                type="date"
                name="date_to"
                value={filters.date_to}
                onChange={handleFilterChange}
              />
            </div>
          </div>
          <div className="ar-filter-checkboxes">
            <label className="ar-checkbox-label">
              <input
                type="checkbox"
                name="overdue"
                checked={filters.overdue}
                onChange={handleFilterChange}
              />
              <span>Show only overdue tickets</span>
            </label>
            <label className="ar-checkbox-label">
              <input
                type="checkbox"
                name="follow_up"
                checked={filters.follow_up}
                onChange={handleFilterChange}
              />
              <span>Follow-up needed</span>
            </label>
          </div>
        </div>
      )}

      {/* Bulk Actions Bar */}
      {selectedTickets.length > 0 && (
        <div className="ar-bulk-actions-bar">
          <div className="ar-bulk-info">
            <i className="fas fa-check-square"></i>
            <span>{selectedTickets.length} ticket{selectedTickets.length > 1 ? 's' : ''} selected</span>
          </div>
          <div className="ar-bulk-actions">
            <button
              className="ar-btn ar-btn-success ar-btn-sm"
              onClick={() => { setBulkAction('status'); setBulkStatus('completed'); setShowBulkModal(true); }}
            >
              <i className="fas fa-check"></i> Complete
            </button>
            <button
              className="ar-btn ar-btn-info ar-btn-sm"
              onClick={() => { setBulkAction('status'); setBulkStatus('in_progress'); setShowBulkModal(true); }}
            >
              <i className="fas fa-play"></i> Start Progress
            </button>
            <button
              className="ar-btn ar-btn-warning ar-btn-sm"
              onClick={() => { setBulkAction('priority'); setShowBulkModal(true); }}
            >
              <i className="fas fa-flag"></i> Change Priority
            </button>
            <button
              className="ar-btn ar-btn-purple ar-btn-sm"
              onClick={() => { setBulkAction('assign'); setShowBulkModal(true); }}
            >
              <i className="fas fa-user-plus"></i> Assign
            </button>
            <button
              className="ar-btn ar-btn-danger ar-btn-sm"
              onClick={() => { setBulkAction('status'); setBulkStatus('cancelled'); setShowBulkModal(true); }}
            >
              <i className="fas fa-ban"></i> Cancel
            </button>
            <button
              className="ar-btn ar-btn-outline ar-btn-sm"
              onClick={() => setSelectedTickets([])}
            >
              Clear Selection
            </button>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="ar-content-area">
        {isLoading ? (
          <div className="ar-loading-state">
            <div className="ar-loading-spinner">
              <div className="ar-spinner-ring"></div>
              <p>Loading tickets...</p>
            </div>
          </div>
        ) : filteredTickets.length === 0 ? (
          <div className="ar-empty-state">
            <div className="ar-empty-icon">
              <i className="fas fa-search"></i>
            </div>
            <h3>No tickets found</h3>
            <p>
              {getActiveFilterCount() > 0
                ? 'Try adjusting your filters or search criteria'
                : 'No maintenance requests found. Create your first request!'}
            </p>
            <div className="ar-empty-actions">
              {getActiveFilterCount() > 0 ? (
                <button className="ar-btn ar-btn-outline" onClick={clearAllFilters}>
                  Clear All Filters
                </button>
              ) : (
                <button className="ar-btn ar-btn-primary" onClick={() => history.push('/maintenance/new-request')}>
                  <i className="fas fa-plus"></i> Create New Request
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="ar-table-container" ref={tableRef}>
            <div className="ar-table-wrapper">
              <table className="ar-table">
                <thead>
                  <tr>
                    <th className="ar-th-checkbox">
                      <input
                        type="checkbox"
                        checked={selectAll}
                        onChange={handleSelectAll}
                      />
                    </th>
                    <th onClick={() => handleSort('ticket_number')} className="ar-sortable">
                      Ticket #
                      {sortConfig.key === 'ticket_number' && (
                        <i className={`fas fa-sort-${sortConfig.direction === 'asc' ? 'up' : 'down'} ar-sort-icon`}></i>
                      )}
                    </th>
                    <th onClick={() => handleSort('type')} className="ar-sortable">
                      Type
                      {sortConfig.key === 'type' && (
                        <i className={`fas fa-sort-${sortConfig.direction === 'asc' ? 'up' : 'down'} ar-sort-icon`}></i>
                      )}
                    </th>
                    <th onClick={() => handleSort('requested_by')} className="ar-sortable">
                      Requested By
                      {sortConfig.key === 'requested_by' && (
                        <i className={`fas fa-sort-${sortConfig.direction === 'asc' ? 'up' : 'down'} ar-sort-icon`}></i>
                      )}
                    </th>
                    <th>Issue Description</th>
                    <th onClick={() => handleSort('priority')} className="ar-sortable">
                      Priority
                    </th>
                    <th onClick={() => handleSort('status')} className="ar-sortable">
                      Status
                    </th>
                    <th>Approval</th>
                    <th onClick={() => handleSort('assigned_to')} className="ar-sortable">
                      Assigned To
                    </th>
                    <th onClick={() => handleSort('created_at')} className="ar-sortable">
                      Created
                      {sortConfig.key === 'created_at' && (
                        <i className={`fas fa-sort-${sortConfig.direction === 'asc' ? 'up' : 'down'} ar-sort-icon`}></i>
                      )}
                    </th>
                    <th>Due Date</th>
                    <th className="ar-th-actions">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.map((ticket) => {
                    const isRejected = isCompletionRejected(ticket);
                    const statusLabel = getStatusLabel(ticket);
                    const priorityColor = getPriorityColor(ticket.priority);
                    const overdue = isOverdue(ticket);
                    const dueDate = getDueDate(ticket);
                    const daysRemaining = getDaysRemaining(ticket);
                    const rowClass = getRowClass(ticket);

                    return (
                      <tr
                        key={ticket.id}
                        className={rowClass}
                      >
                        <td className="ar-td-checkbox">
                          <input
                            type="checkbox"
                            checked={selectedTickets.includes(ticket.id)}
                            onChange={() => handleSelectTicket(ticket.id)}
                          />
                        </td>
                        <td className="ar-td-ticket-number">
                          <Link to={`/maintenance/request/${ticket.id}`} className="ar-ticket-link">
                            {ticket.ticket_number}
                          </Link>
                          {ticket.is_recurring && (
                            <span className="ar-recurring-badge" title={`Recurring: ${ticket.recurrence_type || 'monthly'}`}>
                              <i className="fas fa-sync-alt"></i>
                            </span>
                          )}
                          {isRejected && (
                            <span className="ar-rejected-badge">
                              <i className="fas fa-times-circle"></i> Rejected
                            </span>
                          )}
                        </td>
                        <td>
                          <span className={`ar-type-badge ${ticket.type}`}>
                            {ticket.type === 'prescheduled' ? (
                              <>
                                <i className="fas fa-calendar-alt"></i> Pre-scheduled
                                {ticket.is_recurring && (
                                  <span className="ar-recurring-label">
                                    ({ticket.recurrence_type || 'monthly'})
                                  </span>
                                )}
                              </>
                            ) : (
                              <>
                                <i className="fas fa-exclamation-triangle"></i> Emergent
                              </>
                            )}
                          </span>
                        </td>
                        <td className="ar-td-requested-by">
                          {ticket.requested_by || 'Unknown'}
                        </td>
                        <td className="ar-td-issue">
                          <div className="ar-issue-description" title={ticket.issue_description}>
                            {ticket.issue_description || 'No description'}
                          </div>
                          {ticket.maintenance_category && (
                            <div className="ar-issue-category">{ticket.maintenance_category}</div>
                          )}
                          {isRejected && ticket.manager_comments && (
                            <div className="ar-rejection-feedback">
                              <i className="fas fa-comment"></i> {ticket.manager_comments}
                            </div>
                          )}
                        </td>
                        <td>
                          <span
                            className="ar-priority-badge"
                            style={{
                              background: priorityColor.bg,
                              color: priorityColor.text
                            }}
                          >
                            {priorityColor.icon} {ticket.priority || 'Low'}
                          </span>
                        </td>
                        <td>
                          <span className={`ar-status-badge ar-status-${ticket.status || 'unknown'}`}>
                            {statusLabel}
                          </span>
                        </td>
                        <td>
                          <span className={`ar-approval-badge ar-approval-${ticket.approval_status?.toLowerCase() || 'pending'}`}>
                            {ticket.approval_status || 'Pending'}
                          </span>
                        </td>
                        <td>
                          {ticket.assigned_to ? (
                            <div className="ar-assignee">
                              <div className="ar-assignee-avatar">
                                {ticket.assigned_to.charAt(0).toUpperCase()}
                              </div>
                              <span>{ticket.assigned_to}</span>
                            </div>
                          ) : (
                            <span className="ar-unassigned">Unassigned</span>
                          )}
                        </td>
                        <td>
                          <div className="ar-date-info" title={new Date(ticket.created_at).toLocaleString()}>
                            {formatDate(ticket.created_at)}
                          </div>
                        </td>
                        <td>
                          {dueDate ? (
                            <div className={`ar-due-date ${overdue ? 'overdue' : daysRemaining <= 3 ? 'warning' : ''}`}>
                              <i className={`fas fa-${overdue ? 'exclamation-triangle' : 'calendar-check'}`}></i>
                              {formatDate(dueDate)}
                              {daysRemaining !== null && !isRejected && (
                                <span className="ar-days-remaining">
                                  ({daysRemaining === 0 ? 'Today' : daysRemaining < 0 ? `${Math.abs(daysRemaining)} days overdue` : `${daysRemaining} days left`})
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="ar-no-due-date">Not set</span>
                          )}
                        </td>
                        <td className="ar-td-actions">
                          <div className="ar-action-buttons">
                            <Link
                              to={`/maintenance/request/${ticket.id}`}
                              className="ar-action-btn ar-btn-view"
                              title="View Details"
                            >
                              <i className="fas fa-eye"></i>
                            </Link>
                            {isManager && ticket.approval_status === 'Pending' && (
                              <>
                                <button
                                  className="ar-action-btn ar-btn-approve"
                                  onClick={() => openApprovalModal(ticket)}
                                  title="Approve Request"
                                >
                                  <i className="fas fa-check"></i>
                                </button>
                                <button
                                  className="ar-action-btn ar-btn-reject"
                                  onClick={() => openRejectModal(ticket)}
                                  title="Reject Request"
                                >
                                  <i className="fas fa-times"></i>
                                </button>
                              </>
                            )}
                            {isRejected && (
                              <Link
                                to={`/maintenance/request/${ticket.id}`}
                                className="ar-action-btn ar-btn-reopen"
                                title="Reopen Ticket"
                              >
                                <i className="fas fa-undo"></i>
                              </Link>
                            )}
                            <Link
                              to={`/maintenance/request/${ticket.id}/edit`}
                              className="ar-action-btn ar-btn-edit"
                              title="Edit Ticket"
                            >
                              <i className="fas fa-edit"></i>
                            </Link>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Pagination */}
      {filteredTickets.length > 0 && (
        <div className="ar-pagination-container">
          <div className="ar-pagination-info">
            Showing <strong>{indexOfFirstItem + 1}-{Math.min(indexOfLastItem, filteredTickets.length)}</strong> of <strong>{filteredTickets.length}</strong> tickets
          </div>
          <div className="ar-pagination-controls">
            <button
              className="ar-page-btn"
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
            >
              <i className="fas fa-angle-double-left"></i>
            </button>
            <button
              className="ar-page-btn"
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
            >
              <i className="fas fa-angle-left"></i>
            </button>

            {getPageNumbers().map(page => (
              <button
                key={page}
                className={`ar-page-btn ${currentPage === page ? 'active' : ''}`}
                onClick={() => setCurrentPage(page)}
              >
                {page}
              </button>
            ))}

            <button
              className="ar-page-btn"
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
            >
              <i className="fas fa-angle-right"></i>
            </button>
            <button
              className="ar-page-btn"
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
            >
              <i className="fas fa-angle-double-right"></i>
            </button>
          </div>
        </div>
      )}

      {/* Approve Modal */}
      {showApprovalModal && (
        <div className="ar-modal-overlay" onClick={() => setShowApprovalModal(false)}>
          <div className="ar-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ar-modal-header">
              <h3><i className="fas fa-check-circle"></i> Approve Request</h3>
              <button className="ar-modal-close" onClick={() => setShowApprovalModal(false)}>×</button>
            </div>
            <div className="ar-modal-body">
              <div className="ar-approval-summary">
                <div className="ar-summary-item">
                  <strong>Ticket:</strong> {selectedTicketForApproval?.ticket_number}
                </div>
                <div className="ar-summary-item">
                  <strong>Type:</strong> {selectedTicketForApproval?.type === 'prescheduled' ? 'Pre-scheduled' : 'Emergent'}
                </div>
                <div className="ar-summary-item">
                  <strong>Issue:</strong> {selectedTicketForApproval?.issue_description}
                </div>
                <div className="ar-summary-item">
                  <strong>Requested By:</strong> {selectedTicketForApproval?.requested_by}
                </div>
                {selectedTicketForApproval?.type === 'prescheduled' && (
                  <div className="ar-summary-item">
                    <strong>Schedule Start:</strong> {selectedTicketForApproval?.schedule_start_date}
                    {selectedTicketForApproval?.is_recurring && (
                      <span className="ar-recurring-info"> (Recurring: {selectedTicketForApproval?.recurrence_type})</span>
                    )}
                  </div>
                )}
                {selectedTicketForApproval?.approval_type === 'completion' && selectedTicketForApproval?.completion_notes && (
                  <div className="ar-summary-item">
                    <strong>Completion Notes:</strong> {selectedTicketForApproval?.completion_notes}
                  </div>
                )}
                {selectedTicketForApproval?.approval_type === 'modification' && selectedTicketForApproval?.pending_data && (
                  <div className="ar-summary-item">
                    <strong>Modification:</strong> Changes pending approval
                    <button
                      className="ar-btn ar-btn-sm ar-btn-outline"
                      onClick={() => {
                        try {
                          const changes = JSON.parse(selectedTicketForApproval.pending_data);
                          alert('Proposed Changes:\n' + JSON.stringify(changes, null, 2));
                        } catch (e) {
                          alert(selectedTicketForApproval.pending_data);
                        }
                      }}
                      style={{ marginLeft: '8px' }}
                    >
                      View Changes
                    </button>
                  </div>
                )}
              </div>
              <div className="ar-form-group">
                <label>Completion Date <span className="ar-required">*</span></label>
                <input
                  type="date"
                  className="ar-form-input"
                  value={approvalData.assigned_completion_date}
                  onChange={(e) => setApprovalData(prev => ({ ...prev, assigned_completion_date: e.target.value }))}
                  min={new Date().toISOString().slice(0, 10)}
                />
              </div>
              <div className="ar-form-group">
                <label>Manager Comments</label>
                <textarea
                  className="ar-form-textarea"
                  rows="3"
                  value={approvalData.manager_comments}
                  onChange={(e) => setApprovalData(prev => ({ ...prev, manager_comments: e.target.value }))}
                  placeholder="Add instructions or notes..."
                />
              </div>
            </div>
            <div className="ar-modal-footer">
              <button className="ar-btn ar-btn-outline" onClick={() => setShowApprovalModal(false)}>Cancel</button>
              <button
                className="ar-btn ar-btn-success"
                onClick={handleApprove}
                disabled={!approvalData.assigned_completion_date || isSubmitting}
              >
                {isSubmitting ? 'Processing...' : 'Approve Request'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="ar-modal-overlay" onClick={() => setShowRejectModal(false)}>
          <div className="ar-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ar-modal-header">
              <h3><i className="fas fa-times-circle"></i> Reject Request</h3>
              <button className="ar-modal-close" onClick={() => setShowRejectModal(false)}>×</button>
            </div>
            <div className="ar-modal-body">
              <div className="ar-approval-summary">
                <div className="ar-summary-item">
                  <strong>Ticket:</strong> {selectedTicketForApproval?.ticket_number}
                </div>
                <div className="ar-summary-item">
                  <strong>Issue:</strong> {selectedTicketForApproval?.issue_description}
                </div>
                {selectedTicketForApproval?.approval_type === 'completion' && selectedTicketForApproval?.completion_notes && (
                  <div className="ar-summary-item">
                    <strong>Completion Notes:</strong> {selectedTicketForApproval?.completion_notes}
                  </div>
                )}
                {selectedTicketForApproval?.approval_type === 'modification' && selectedTicketForApproval?.pending_data && (
                  <div className="ar-summary-item">
                    <strong>Modification:</strong> Changes pending approval
                    <button
                      className="ar-btn ar-btn-sm ar-btn-outline"
                      onClick={() => {
                        try {
                          const changes = JSON.parse(selectedTicketForApproval.pending_data);
                          alert('Proposed Changes:\n' + JSON.stringify(changes, null, 2));
                        } catch (e) {
                          alert(selectedTicketForApproval.pending_data);
                        }
                      }}
                      style={{ marginLeft: '8px' }}
                    >
                      View Changes
                    </button>
                  </div>
                )}
              </div>
              <div className="ar-form-group">
                <label>Rejection Reason <span className="ar-required">*</span></label>
                <textarea
                  className="ar-form-textarea"
                  rows="4"
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Explain why this request is being rejected..."
                />
              </div>
              <div className="ar-rejection-info">
                <i className="fas fa-info-circle"></i>
                <span>
                  {selectedTicketForApproval?.approval_type === 'completion'
                    ? 'Rejecting this completion will keep the ticket in its current state. The maintenance team can reopen it to fix and resubmit.'
                    : 'Rejecting this request will close it. The requester will need to submit a new request.'}
                </span>
              </div>
            </div>
            <div className="ar-modal-footer">
              <button className="ar-btn ar-btn-outline" onClick={() => setShowRejectModal(false)}>Cancel</button>
              <button
                className="ar-btn ar-btn-danger"
                onClick={handleReject}
                disabled={!rejectionReason.trim() || isSubmitting}
              >
                {isSubmitting ? 'Processing...' : 'Reject Request'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Action Modal */}
      {showBulkModal && (
        <div className="ar-modal-overlay" onClick={() => setShowBulkModal(false)}>
          <div className="ar-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ar-modal-header">
              <h3>Bulk Action</h3>
              <button className="ar-modal-close" onClick={() => setShowBulkModal(false)}>×</button>
            </div>
            <div className="ar-modal-body">
              <p>Apply action to <strong>{selectedTickets.length}</strong> selected tickets</p>
              <div className="ar-form-group">
                <label>Action Type</label>
                <select className="ar-form-input" value={bulkAction} onChange={(e) => setBulkAction(e.target.value)}>
                  <option value="status">Change Status</option>
                  <option value="priority">Change Priority</option>
                  <option value="assign">Assign To</option>
                </select>
              </div>
              {bulkAction === 'status' && (
                <div className="ar-form-group">
                  <label>New Status</label>
                  <select className="ar-form-input" value={bulkStatus} onChange={(e) => setBulkStatus(e.target.value)}>
                    <option value="">Select Status</option>
                    <option value="open">Open</option>
                    <option value="in_progress">In Progress</option>
                    <option value="review">Review</option>
                    <option value="completed">Completed</option>
                    <option value="closed">Closed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
              )}
              {bulkAction === 'priority' && (
                <div className="ar-form-group">
                  <label>New Priority</label>
                  <select className="ar-form-input" value={bulkPriority} onChange={(e) => setBulkPriority(e.target.value)}>
                    <option value="">Select Priority</option>
                    <option value="Critical">Critical</option>
                    <option value="High">High</option>
                    <option value="Medium">Medium</option>
                    <option value="Low">Low</option>
                  </select>
                </div>
              )}
              {bulkAction === 'assign' && (
                <div className="ar-form-group">
                  <label>Assign To</label>
                  <input
                    type="text"
                    className="ar-form-input"
                    value={bulkAssignee}
                    onChange={(e) => setBulkAssignee(e.target.value)}
                    placeholder="Enter assignee name..."
                  />
                </div>
              )}
            </div>
            <div className="ar-modal-footer">
              <button className="ar-btn ar-btn-outline" onClick={() => setShowBulkModal(false)}>Cancel</button>
              <button
                className="ar-btn ar-btn-primary"
                onClick={handleBulkAction}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Processing...' : 'Apply'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Export Modal */}
      {showExportModal && (
        <div className="ar-modal-overlay" onClick={() => setShowExportModal(false)}>
          <div className="ar-modal ar-modal-sm" onClick={(e) => e.stopPropagation()}>
            <div className="ar-modal-header">
              <h3>Export Tickets</h3>
              <button className="ar-modal-close" onClick={() => setShowExportModal(false)}>×</button>
            </div>
            <div className="ar-modal-body">
              <p>Choose export format:</p>
              <div className="ar-export-options">
                <button
                  className="ar-export-option"
                  onClick={() => handleExport('csv')}
                  disabled={exportLoading}
                >
                  <i className="fas fa-file-csv"></i>
                  <span>CSV</span>
                </button>
                <button
                  className="ar-export-option"
                  onClick={() => handleExport('xlsx')}
                  disabled={exportLoading}
                >
                  <i className="fas fa-file-excel"></i>
                  <span>Excel</span>
                </button>
                <button
                  className="ar-export-option"
                  onClick={() => handleExport('pdf')}
                  disabled={exportLoading}
                >
                  <i className="fas fa-file-pdf"></i>
                  <span>PDF</span>
                </button>
              </div>
              {exportLoading && <p className="ar-export-loading">Preparing export...</p>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AllRequests;